import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Topbar } from "../ui/Topbar";
import { useScreener } from "../features/screener/useScreener";
import { useFavorites } from "../features/screener/useFavorites";
import { useHidden } from "../features/screener/useHidden";
import { ago, useCacheStatus } from "../features/screener/useCacheStatus";
import { FilterBar, type Filters } from "../features/screener/FilterBar";
import { ScreenerTable } from "../features/screener/ScreenerTable";
import { usePortfolio } from "../features/portfolio/usePortfolio";
import { AddPositionModal } from "../features/portfolio/AddPositionModal";
import { AddAlertModal } from "../features/alerts/AddAlertModal";
import { StagesProgress } from "../ui/loaders";
import { monthsUntil } from "../lib/format";
import { useScrollRestore } from "../lib/useScrollRestore";

const INITIAL_FILTERS: Filters = {
  board: "TQCB",
  currency: "RUB",
  target: 0.16,
  tax: "resident",
  maxMonths: null,
  favoritesOnly: false,
  hideUnavailable: true,
  search: "",
};

// Модульный кэш фильтров — переживает unmount/mount при «назад» из карточки
// бумаги. Сбрасывается на дефолт только при полной перезагрузке страницы.
let savedFilters: Filters = INITIAL_FILTERS;

export default function Screener() {
  const [filters, setFilters] = useState<Filters>(savedFilters);
  useEffect(() => {
    savedFilters = filters;
  }, [filters]);
  const screen = useScreener({
    // Борт задаёт валютный/типовой таб фильтра: TQCB — корпораты (RUB/USD/CNY,
    // замещайки и юаневые сидят там же), TQOB — ОФЗ и субфеды. TQOD — легаси
    // USD-евробонды, к 2026 почти пустой, отдельной веткой не держим.
    board: filters.board,
    target: filters.target,
    tax: filters.tax,
    currency: filters.currency,
  });
  const { favorites, toggle } = useFavorites();
  const { hidden, toggle: toggleHidden } = useHidden();
  const portfolio = usePortfolio();
  const cache = useCacheStatus();
  const navigate = useNavigate();
  const scrollRef = useScrollRestore<HTMLDivElement>("screener");
  const [addingSecid, setAddingSecid] = useState<string | null>(null);
  const [alertSecid, setAlertSecid] = useState<string | null>(null);

  const inPortfolio = useMemo(
    () =>
      new Set(
        portfolio.data?.positions.map((p) => p.position.secid) ?? [],
      ),
    [portfolio.data],
  );

  const update = (next: Partial<Filters>) =>
    setFilters((prev) => ({ ...prev, ...next }));

  const filtered = useMemo(() => {
    const rows = screen.data;
    if (!rows) return [];
    const q = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.maxMonths != null) {
        const m = monthsUntil(r.yield_horizon);
        if (m == null || m > filters.maxMonths) return false;
      }
      if (filters.favoritesOnly && !favorites.has(r.secid)) return false;
      if (filters.hideUnavailable && hidden.has(r.secid)) return false;
      if (q) {
        const hay = [r.secid, r.short_name, r.secname ?? ""]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [screen.data, filters, favorites, hidden]);

  const okCount = filtered.filter((r) => r.net_ytm >= filters.target).length;
  const total = screen.data?.length ?? 0;

  return (
    <>
      <Topbar
        title="Скринер облигаций"
        right={
          cache && (
            <span className="cache-status">
              <span>♻ котировки: {ago(cache.quotes_at)}</span>
              <span>♻ расписания: {ago(cache.schedules_at)}</span>
            </span>
          )
        }
      />

      <FilterBar
        filters={filters}
        onChange={update}
        favCount={favorites.size}
        hiddenCount={hidden.size}
      />

      {screen.isPending && (
        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <StagesProgress
            stages={["Котировки", "Расписания", "Расчёт IRR", "Фильтр"]}
          />
        </div>
      )}

      {screen.isError && (
        <div className="banner error">ошибка: {String(screen.error)}</div>
      )}

      {screen.data && (
        <>
          <div className="row" style={{ margin: "0 0 8px" }}>
            <span className="soft">показано&nbsp;</span>
            <b>{filtered.length}</b>
            {filtered.length !== total && (
              <span className="soft">&nbsp;из {total}</span>
            )}
            <span className="soft" style={{ marginLeft: 12 }}>
              OK (≥{(filters.target * 100).toFixed(0)}%):&nbsp;
            </span>
            <b className="ok">{okCount}</b>
            {screen.isFetching && (
              <span className="soft" style={{ marginLeft: 12 }}>
                · обновляю…
              </span>
            )}
          </div>
          <div ref={scrollRef} className="page-scroll">
            <ScreenerTable
              rows={filtered}
              target={filters.target}
              favorites={favorites}
              hidden={hidden}
              inPortfolio={inPortfolio}
              onToggleFav={toggle}
              onToggleHidden={toggleHidden}
              onAddToPortfolio={(secid) => setAddingSecid(secid)}
              onAddAlert={(secid) => setAlertSecid(secid)}
              onPick={(secid) => navigate(`/bond/${secid}`)}
            />
          </div>
        </>
      )}

      {addingSecid && (
        <AddPositionModal
          secid={addingSecid}
          onClose={() => setAddingSecid(null)}
        />
      )}

      {alertSecid && (() => {
        const row = screen.data?.find((r) => r.secid === alertSecid);
        if (!row) return null;
        return (
          <AddAlertModal
            secid={row.secid}
            secname={row.secname ?? row.short_name}
            currentPrice={row.last_price}
            onClose={() => setAlertSecid(null)}
          />
        );
      })()}
    </>
  );
}
